import { useEffect, useRef, useState } from "react";
import { Mic, MicOff, Video, VideoOff, PhoneOff, Phone, SwitchCamera, Minimize2, Maximize2 } from "lucide-react";
import { useTalk, TkAvatar } from "./TalkLayout";

function Media({ stream, muted, className, testId }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current && ref.current.srcObject !== stream) ref.current.srcObject = stream || null; }, [stream]);
  return <video ref={ref} autoPlay playsInline muted={muted} className={className} data-testid={testId} />;
}

function useTimer(start) {
  const [, tick] = useState(0);
  useEffect(() => { if (!start) return; const t = setInterval(() => tick((x) => x + 1), 1000); return () => clearInterval(t); }, [start]);
  if (!start) return "";
  const s = Math.floor((Date.now() - start) / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

const Btn = ({ onClick, children, danger, ok, active, testId, label }) => (
  <button onClick={onClick} data-testid={testId} aria-label={label}
    className={`w-14 h-14 rounded-full flex items-center justify-center transition-transform active:scale-90 ${danger ? "bg-[#FF003C] text-white" : ok ? "bg-[#39FF14] text-black" : active ? "bg-white text-black" : "bg-white/15 text-white backdrop-blur"}`}>
    {children}
  </button>
);

export default function CallOverlay() {
  const { engine, call, subscribe } = useTalk();
  const [local, setLocal] = useState(null);
  const [remote, setRemote] = useState(null);
  const [mini, setMini] = useState(false);
  const timer = useTimer(call?.connectedAt);
  useEffect(() => subscribe((t, s) => { if (t === "local") setLocal(s); if (t === "remote") setRemote(s); if (t === "call" && !s) { setLocal(null); setRemote(null); setMini(false); } }), [subscribe]);
  if (!call || !engine) return null;
  const label = { calling: "Calling…", ringing: call.calleeOnline === false ? "Ringing their phone…" : "Ringing…", incoming: `Incoming ${call.video ? "video" : "voice"} call`, connecting: "Connecting…", active: timer }[call.state] || "";
  const showVideo = call.video && (call.state === "active" || call.state === "connecting");

  if (mini) return (
    <button onClick={() => setMini(false)} data-testid="talk-call-mini" className="fixed bottom-24 right-4 z-[70] flex items-center gap-2 bg-[#39FF14] text-black font-black rounded-full pl-2 pr-4 py-2 shadow-2xl">
      <TkAvatar p={call.other} size={32} /> {label} <Maximize2 className="w-4 h-4" />
    </button>
  );

  return (
    <div className="fixed inset-0 z-[70] bg-[#050509] flex flex-col" data-testid="talk-call-overlay" data-state={call.state}>
      {showVideo && <Media stream={remote} className="absolute inset-0 w-full h-full object-cover" testId="talk-remote-video" />}
      {!showVideo && <Media stream={remote} className="hidden" testId="talk-remote-audio" />}
      {showVideo && local && <Media stream={local} muted className="absolute top-20 right-4 w-28 sm:w-40 aspect-[3/4] object-cover rounded-2xl border-2 border-white/20 z-10 -scale-x-100" testId="talk-local-video" />}
      <div className="relative z-10 flex items-center justify-between p-4">
        <span className="mono text-[10px] uppercase tracking-widest text-white/60">{call.video ? "Video call" : "Voice call"} · end-to-end peer</span>
        {call.state === "active" && <button onClick={() => setMini(true)} data-testid="talk-call-minimize" className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center"><Minimize2 className="w-4 h-4" /></button>}
      </div>
      <div className={`relative z-10 flex-1 flex flex-col items-center ${showVideo && remote ? "justify-start pt-4" : "justify-center"}`}>
        {!(showVideo && remote) && <div className={call.state === "incoming" || call.state === "ringing" ? "animate-pulse" : ""}><TkAvatar p={call.other} size={120} /></div>}
        <div className="font-heading font-black text-3xl mt-5 drop-shadow" data-testid="talk-call-name">{call.other?.display_name}</div>
        <div className="text-white/70 mt-1 mono text-sm" data-testid="talk-call-status">{label}</div>
        {call.remoteCamOff && showVideo && <div className="text-xs text-white/60 mt-2">Their camera is off</div>}
      </div>
      <div className="relative z-10 pb-12 pt-6 flex items-center justify-center gap-5 bg-gradient-to-t from-black/80 to-transparent">
        {call.state === "incoming" ? (
          <>
            <Btn danger onClick={() => engine.reject()} testId="talk-call-decline" label="Decline"><PhoneOff className="w-6 h-6" /></Btn>
            <Btn ok onClick={() => engine.accept()} testId="talk-call-accept" label="Accept">{call.video ? <Video className="w-6 h-6" /> : <Phone className="w-6 h-6" />}</Btn>
          </>
        ) : (
          <>
            <Btn onClick={() => engine.toggleMute()} active={call.muted} testId="talk-call-mute" label="Mute">{call.muted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}</Btn>
            {call.video && <Btn onClick={() => engine.toggleCam()} active={call.camOff} testId="talk-call-camera" label="Camera">{call.camOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}</Btn>}
            {call.video && <Btn onClick={() => engine.flipCam()} testId="talk-call-flip" label="Switch camera"><SwitchCamera className="w-6 h-6" /></Btn>}
            <Btn danger onClick={() => engine.hangup()} testId="talk-call-hangup" label="Hang up"><PhoneOff className="w-6 h-6" /></Btn>
          </>
        )}
      </div>
    </div>
  );
}
